import { useMemo, useState } from 'react';
import { Loader2, Download, Receipt } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/utils';
import { useAdminTenants } from '@/hooks/use-admin';
import { useAdminCampaignUsage, type CampaignUsageRow } from '@/hooks/use-admin-campaign-usage';

interface AdminTenant {
  id: string;
  name: string;
}

/** to-date is inclusive: upper bound is the start of the following day (UTC). */
function toExclusiveIso(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

function csvCell(v: string | number | null): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Super-admin cross-tenant SMS campaign billing view. Reads the append-only
 * message_usage ledger (via pos-backend's campaign_message_usage_summary view)
 * — the source of truth for per-campaign invoicing. Billable = messages_sent;
 * cost driver = billable_segments (carrier parts).
 */
export function AdminCampaignUsagePage() {
  const [tenantId, setTenantId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const tenantsQuery = useAdminTenants({ perPage: 200 });
  const tenants = useMemo<AdminTenant[]>(
    () => ((tenantsQuery.data?.data ?? []) as AdminTenant[]).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [tenantsQuery.data],
  );
  const tenantName = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenants) m.set(t.id, t.name);
    return m;
  }, [tenants]);

  const usageQuery = useAdminCampaignUsage({
    tenantId: tenantId || undefined,
    from: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
    to: toDate ? toExclusiveIso(toDate) : undefined,
  });
  const rows = useMemo<CampaignUsageRow[]>(() => usageQuery.data?.data ?? [], [usageQuery.data]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.attempted += r.messagesAttempted;
          acc.sent += r.messagesSent;
          acc.failed += r.messagesFailed;
          acc.segments += r.billableSegments;
          return acc;
        },
        { attempted: 0, sent: 0, failed: 0, segments: 0 },
      ),
    [rows],
  );

  function exportCsv() {
    const header = [
      'Business',
      'Campaign',
      'Location',
      'Attempted',
      'Sent',
      'Failed',
      'Billable Segments',
      'First Send',
      'Last Send',
    ];
    const lines = rows.map((r) =>
      [
        tenantName.get(r.tenantId) ?? r.tenantId,
        r.campaignName ?? r.campaignId,
        r.locationName ?? r.locationId ?? '',
        r.messagesAttempted,
        r.messagesSent,
        r.messagesFailed,
        r.billableSegments,
        r.firstMessageAt ?? '',
        r.lastMessageAt ?? '',
      ]
        .map(csvCell)
        .join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign-usage-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const loading = usageQuery.isLoading || tenantsQuery.isLoading;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Campaign Usage & Billing"
        description="Per-campaign SMS message usage across all businesses, from the message_usage ledger. Billable = messages sent; cost is driven by billable segments (carrier parts)."
      />

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Business</span>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="h-9 w-64 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">All businesses</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">From</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="h-9 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">To (inclusive)</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="h-9 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>
        <Button variant="secondary" onClick={exportCsv} disabled={rows.length === 0} className="gap-2">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : usageQuery.isError ? (
        <div className="rounded-xl border border-dashed border-destructive/40 py-16 text-center text-destructive">
          Failed to load campaign usage.
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border py-16 text-muted-foreground">
          <Receipt className="h-6 w-6" />
          No campaign message usage for the selected filters.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Business</th>
                <th className="px-3 py-2 font-medium">Campaign</th>
                <th className="px-3 py-2 font-medium">Location</th>
                <th className="px-3 py-2 text-right font-medium">Attempted</th>
                <th className="px-3 py-2 text-right font-medium">Sent</th>
                <th className="px-3 py-2 text-right font-medium">Failed</th>
                <th className="px-3 py-2 text-right font-medium">Billable Segments</th>
                <th className="px-3 py-2 font-medium">First Send</th>
                <th className="px-3 py-2 font-medium">Last Send</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.campaignId} className="hover:bg-muted/30">
                  <td className="px-3 py-2">{tenantName.get(r.tenantId) ?? r.tenantId}</td>
                  <td className="px-3 py-2 font-medium">{r.campaignName ?? r.campaignId}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.locationName ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.messagesAttempted.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.messagesSent.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {r.messagesFailed.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">
                    {r.billableSegments.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.firstMessageAt ? formatDateTime(r.firstMessageAt) : '—'}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.lastMessageAt ? formatDateTime(r.lastMessageAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-border bg-muted/40 font-semibold">
              <tr>
                <td className="px-3 py-2" colSpan={3}>
                  Total ({rows.length} campaign{rows.length === 1 ? '' : 's'})
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{totals.attempted.toLocaleString()}</td>
                <td className="px-3 py-2 text-right tabular-nums">{totals.sent.toLocaleString()}</td>
                <td className="px-3 py-2 text-right tabular-nums">{totals.failed.toLocaleString()}</td>
                <td className="px-3 py-2 text-right tabular-nums">{totals.segments.toLocaleString()}</td>
                <td className="px-3 py-2" colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
